import { useState, type JSX } from 'react';
import { TodayOvertime } from './Overtime.js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Attendance } from '@dxtech/shared';
import { api } from '../api/client.js';
import { dateForOffset, useApp } from '../state/app.js';
import { LeaveStatusIcon, StatusChip, type WorkStatus } from '../ui/status.js';
import { Spinner } from '../ui/common.js';
import { IconIn, IconOut } from '../ui/icons.js';
import { AwayModal } from '../modals/AwayModal.js';
import { LeaveModal } from '../modals/LeaveModal.js';
import { OfficeMap } from './OfficeMap.js';
import { NoticeBoard } from './NoticeBoard.js';
import { KudosWidget } from './Kudos.js';

function kstHHmm(iso?: string): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(iso));
}
function mmdd(d?: string): string {
  if (!d) return '';
  const [, m, day] = d.split('-');
  return `${Number(m)}/${Number(day)}`;
}
function statusOf(a: Attendance | null): WorkStatus {
  if (!a || !a.checkInAt) return 'absent';
  if (a.checkOutAt) return 'off';
  return 'working';
}

export function Home(): JSX.Element {
  const { flash, me, setScreen } = useApp();
  const attendanceExempt = me.roles.includes('CEO'); // 대표이사: 출퇴근 관리 예외
  const qc = useQueryClient();
  const [awayOpen, setAwayOpen] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);

  const att = useQuery({ queryKey: ['attendance', 'today'], queryFn: () => api.attendanceToday() });
  const bal = useQuery({ queryKey: ['leave', 'balance'], queryFn: () => api.leaveBalance() });
  const mine = useQuery({ queryKey: ['leave', 'mine'], queryFn: () => api.leaveMine() });
  const trips = useQuery({ queryKey: ['trips', 'mine'], queryFn: () => api.tripsMine() });
  // 오늘이 (취소 아닌) 출장 기간에 포함되면 출장 자동 출퇴근 상태로 간주
  const todayDate = dateForOffset(0).date;
  const onTrip = (trips.data?.items ?? []).some((t) => t.status !== '취소' && t.startDate <= todayDate && todayDate <= t.endDate);

  // 출퇴근 변경 시 근무 현황(board)도 즉시 갱신 → 평면도 상태 바로 반영
  const refreshAttendance = () => { qc.invalidateQueries({ queryKey: ['attendance'] }); qc.invalidateQueries({ queryKey: ['board'] }); };
  const checkIn = useMutation({
    mutationFn: () => api.checkIn(),
    onSuccess: () => { flash('출근 처리되었습니다'); refreshAttendance(); },
    onError: (e) => flash(e instanceof Error ? e.message : '출근 실패'),
  });
  const checkOut = useMutation({
    mutationFn: () => api.checkOut(),
    onSuccess: () => { flash('퇴근 처리되었습니다'); refreshAttendance(); },
    onError: (e) => flash(e instanceof Error ? e.message : '퇴근 실패'),
  });
  const cancelCheckOut = useMutation({
    mutationFn: () => api.cancelCheckOut(),
    onSuccess: () => { flash('퇴근이 취소되었습니다'); refreshAttendance(); },
    onError: (e) => flash(e instanceof Error ? e.message : '퇴근 취소 실패'),
  });

  const a = att.data?.item ?? null;
  const myStatus = statusOf(a);
  const displayStatus: WorkStatus = onTrip ? 'trip' : attendanceExempt ? 'exempt' : myStatus;
  const balance = bal.data?.balance;
  const remaining = balance?.remaining ?? 0;
  const granted = balance?.granted ?? 15;
  const usedPct = granted ? Math.round(((balance?.used ?? 0) / granted) * 100) : 0;

  const refresh = () => { qc.invalidateQueries({ queryKey: ['leave'] }); qc.invalidateQueries({ queryKey: ['away'] }); };

  return (
    <div style={{ maxWidth: 1080, margin: '0 auto', animation: 'dxfade .3s ease' }}>
      {/* 공지사항 — 오늘 이후 게시 목록 */}
      <div style={{ marginBottom: 16 }}>
        <NoticeBoard />
      </div>

      {/* 상단 2열 카드 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(270px,1fr))', gap: 16, marginBottom: 16 }}>
        {/* 오늘의 근무 */}
        <div className="dx-card" style={{ padding: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ fontSize: 15, fontWeight: 700 }}>오늘의 근무</div>
            <StatusChip s={displayStatus} />
          </div>
          {attendanceExempt ? (
            <>
              <div style={{ marginBottom: 18, padding: '12px 14px', background: '#F8FAFC', border: '1px solid #EDEFF2', borderRadius: 10, fontSize: 13, color: '#6B7280', lineHeight: 1.6 }}>
                대표이사는 <b style={{ color: '#475569' }}>출퇴근 관리 예외</b> 대상입니다.<br />자리비움·출장 일정만 관리합니다.
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="dx-btn dx-btn-ghost" style={{ flex: 1 }} onClick={() => setAwayOpen(true)}>자리비움</button>
                <button className="dx-btn dx-btn-ghost" style={{ flex: 1 }} onClick={() => setScreen('trip')}>출장 관리</button>
              </div>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 20, marginBottom: 18, alignItems: 'flex-end' }}>
                <div><div style={cap}>출근</div><div className="tnum" style={time}>{kstHHmm(a?.checkInAt)}</div></div>
                <div style={{ width: 1, height: 34, background: '#EDEFF2' }} />
                <div><div style={cap}>퇴근</div><div className="tnum" style={{ ...time, color: a?.checkOutAt ? '#1F2937' : '#D1D5DB' }}>{kstHHmm(a?.checkOutAt)}</div></div>
                <div style={{ marginLeft: 'auto', textAlign: 'right' }}><div style={cap}>상태</div><div style={{ fontSize: 14, fontWeight: 600, color: '#6B7280' }}>{onTrip ? '출장' : myStatus === 'working' ? '근무 중' : myStatus === 'off' ? '퇴근' : '미출근'}</div></div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="dx-btn dx-btn-primary" style={{ flex: 1 }} disabled={!!a?.checkInAt || checkIn.isPending} onClick={() => checkIn.mutate()}><IconIn />출근하기</button>
                {myStatus === 'off' ? (
                  <button className="dx-btn dx-btn-danger-outline" style={{ flex: 1 }} disabled={onTrip || cancelCheckOut.isPending} onClick={() => cancelCheckOut.mutate()}><IconOut />퇴근 취소</button>
                ) : (
                  <button className="dx-btn dx-btn-danger" style={{ flex: 1 }} disabled={myStatus !== 'working' || checkOut.isPending} onClick={() => checkOut.mutate()}><IconOut />퇴근하기</button>
                )}
                <button className="dx-btn dx-btn-ghost" onClick={() => setAwayOpen(true)}>자리비움</button>
              </div>
            </>
          )}
          <TodayOvertime />
        </div>

        {/* 내 연차 */}
        <div className="dx-card" style={{ padding: 20, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div style={{ fontSize: 15, fontWeight: 700 }}>내 연차</div>
            <button className="dx-btn dx-btn-primary" style={{ fontSize: 13, padding: '6px 12px' }} onClick={() => setLeaveOpen(true)}>휴가 신청</button>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, marginBottom: 14 }}>
            <div className="tnum" style={{ fontSize: 36, fontWeight: 700, letterSpacing: '-.03em', lineHeight: 1, color: '#2563EB' }}>{remaining}</div>
            <div style={{ fontSize: 14, color: '#9CA3AF', marginBottom: 4 }}>/ {granted}일 잔여</div>
          </div>
          <div style={{ height: 8, borderRadius: 5, background: '#EDEFF2', overflow: 'hidden', marginBottom: 10 }}>
            <div style={{ width: `${usedPct}%`, height: '100%', background: '#2563EB', borderRadius: 5 }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#6B7280' }}>
            <span>사용 {balance?.used ?? 0}일</span><span>승인대기 {balance?.pending ?? 0}일</span>
          </div>
        </div>
      </div>

      {/* DXTECH 근무 현황 (사무실 평면도) — 기존 카드형 보드는 StatusBoardLegacy 로 보존 */}
      <div style={{ marginBottom: 16 }}>
        <OfficeMap />
      </div>

      {/* 칭찬·감사 카드 — 이번 달 최신 */}
      <div style={{ marginBottom: 16 }}>
        <KudosWidget />
      </div>

      {/* 내 휴가 신청 현황 */}
      <div className="dx-card" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '16px 18px 13px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>내 휴가 신청 현황</div>
        </div>
        {mine.isLoading ? (
          <div style={{ padding: 40, textAlign: 'center' }}><Spinner /></div>
        ) : (mine.data?.items.length ?? 0) === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>신청 내역이 없습니다.</div>
        ) : (
          mine.data!.items.map((l) => (
            <div key={l.id} className="dx-row">
              <div style={{ width: 64, flexShrink: 0, fontSize: 13, fontWeight: 600 }}>{l.type}</div>
              <div style={{ width: 116, flexShrink: 0, fontSize: 13, color: '#6B7280' }}>{mmdd(l.startDate)}{l.endDate !== l.startDate ? ` ~ ${mmdd(l.endDate)}` : ''}</div>
              <div style={{ width: 46, flexShrink: 0, fontSize: 13, color: '#6B7280' }} className="tnum">{l.days}일</div>
              <div style={{ flex: 1, minWidth: 0, fontSize: 13, color: '#6B7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.reason ?? ''}</div>
              <LeaveStatusIcon s={l.status} />
            </div>
          ))
        )}
      </div>

      {awayOpen && <AwayModal onClose={() => setAwayOpen(false)} onDone={(m) => { flash(m); refresh(); }} />}
      {leaveOpen && <LeaveModal remaining={remaining} onClose={() => setLeaveOpen(false)} onDone={(m) => { flash(m); refresh(); }} />}
    </div>
  );
}

const cap = { fontSize: 11, color: '#9CA3AF', fontWeight: 600, marginBottom: 3 } as const;
const time = { fontSize: 23, fontWeight: 700, letterSpacing: '-.02em' } as const;
